// Shared, presentational audit-trail timeline.
//
// Renders a record's change history (or a slice of the project-wide audit
// log) as a vertical timeline with a field-level before/after diff for
// edits. Pure presentational: it takes already-fetched entries and never
// touches the network, so both the designer preview and the standalone
// runtime client can render an identical timeline from their own API call.
//
// Theme-neutral inline styles (no external CSS) keep it portable across the
// two separate Vite builds that consume @twasta/page-runtime.

import React from 'react'

export type AuditAction = 'add' | 'edit' | 'delete' | string

export interface AuditChange {
  // field db_name -> [oldValue, newValue]
  [field: string]: [unknown, unknown]
}

export interface AuditEntry {
  id?: number | string
  action: AuditAction
  obj_name?: string | null
  table_name?: string | null
  record_pk?: string | null
  pk?: Record<string, unknown> | null
  old_values?: Record<string, unknown> | null
  new_values?: Record<string, unknown> | null
  changed_fields?: AuditChange | null
  /** db_name (lower-cased) → friendly screen caption, for humanising the diff. */
  field_labels?: Record<string, string> | null
  user_id?: string | null
  user_email?: string | null
  short_user_id?: string | null
  client_ip?: string | null
  /** Interface the change came through: web / assistant / integration / … */
  client?: string | null
  /** The object the change was made THROUGH — a Transaction Wizard's name
   *  when `client` is "wizard". */
  via_object?: string | null
  changed_at?: string | null
}

const ACTION_META: Record<string, { label: string; color: string; bg: string }> = {
  add: { label: 'Created', color: '#0f7b3f', bg: '#e6f5ec' },
  edit: { label: 'Edited', color: '#9a6700', bg: '#fdf3e2' },
  delete: { label: 'Deleted', color: '#b42318', bg: '#fdecea' },
}

/** A button run on the record is logged as `action:<button name>` — an action
 *  on the record, not an edit of it (see record_history.action_run_entry). */
const ACTION_RUN_PREFIX = 'action:'

export function actionRunName(action: AuditAction): string | null {
  const a = String(action || '')
  return a.toLowerCase().startsWith(ACTION_RUN_PREFIX) ? a.slice(ACTION_RUN_PREFIX.length) : null
}

function actionMeta(action: AuditAction) {
  const run = actionRunName(action)
  if (run !== null) return { label: `Ran ${run || 'an action'}`, color: '#3538cd', bg: '#eef0ff' }
  return ACTION_META[String(action).toLowerCase()] || {
    label: String(action || 'Changed'), color: '#475467', bg: '#eef1f4',
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '∅'
  if (typeof v === 'object') {
    try { return JSON.stringify(v) } catch { return String(v) }
  }
  return String(v)
}

/** True when old/new render identically once padding is trimmed and
 *  null/empty are unified — a phantom change (e.g. a CHAR-padded primary key).
 *  The backend already strips these; this guards entries from an older one. */
function sameValue(a: unknown, b: unknown): boolean {
  return formatValue(a).trim() === formatValue(b).trim()
}

function formatWhen(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  try {
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function actor(e: AuditEntry): string {
  return (e.user_email || e.short_user_id || e.user_id || 'unknown') as string
}

function DiffRow({ field, from, to }: { field: string; from: unknown; to: unknown }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 14 }}>
      <span style={{ fontWeight: 600, color: '#344054' }}>{field}</span>
      <span style={{ color: '#b42318', textDecoration: 'line-through', wordBreak: 'break-word' }}>
        {formatValue(from)}
      </span>
      <span style={{ color: '#98a2b3' }}>→</span>
      <span style={{ color: '#0f7b3f', wordBreak: 'break-word' }}>{formatValue(to)}</span>
    </div>
  )
}

/** Field-level diff body for one entry (exported for reuse in tables). */
export function AuditDiff({ entry }: { entry: AuditEntry }) {
  // Prefer the friendly screen caption; fall back to the raw db_name.
  const labelFor = (field: string): string =>
    entry.field_labels?.[field] ?? entry.field_labels?.[field.toLowerCase()] ?? field
  const act = String(entry.action).toLowerCase()
  const isRun = actionRunName(entry.action) !== null
  if (act === 'edit' || isRun) {
    const changes = entry.changed_fields || {}
    // Skip no-op rows (identical old/new) in case an older backend didn't.
    const keys = Object.keys(changes).filter((k) => !sameValue(changes[k][0], changes[k][1]))
    if (keys.length === 0) {
      return (
        <div style={{ fontSize: 14, color: '#667085' }}>
          {isRun ? 'The record was not changed.' : 'No field changes recorded.'}
        </div>
      )
    }
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        {keys.map((k) => (
          <DiffRow key={k} field={labelFor(k)} from={changes[k][0]} to={changes[k][1]} />
        ))}
      </div>
    )
  }
  // add / delete: show the snapshot we have (new for add, old for delete).
  const snapshot = act === 'delete' ? entry.old_values : entry.new_values
  const rows = Object.entries(snapshot || {})
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 14, color: '#667085' }}>
        {act === 'delete' ? 'Record removed.' : 'Record created.'}
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 6, fontSize: 14 }}>
          <span style={{ fontWeight: 600, color: '#344054' }}>{labelFor(k)}</span>
          <span style={{ color: '#475467', wordBreak: 'break-word' }}>{formatValue(v)}</span>
        </div>
      ))}
    </div>
  )
}

export interface AuditTimelineProps {
  entries: AuditEntry[]
  emptyText?: string
  /** Show the object name on each entry (useful in the project-wide log). */
  showObject?: boolean
}

/** Vertical change-history timeline (newest first — caller's order is kept). */
export function AuditTimeline({ entries, emptyText, showObject }: AuditTimelineProps) {
  if (!entries || entries.length === 0) {
    return (
      <div style={{ padding: '16px 4px', color: '#667085', fontSize: 15 }}>
        {emptyText || 'No changes recorded yet.'}
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 0 }}>
      {entries.map((e, i) => {
        const meta = actionMeta(e.action)
        const last = i === entries.length - 1
        return (
          <div key={e.id ?? i} style={{ display: 'flex', gap: 12 }}>
            {/* rail */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{
                width: 12, height: 12, borderRadius: '50%',
                background: meta.color, marginTop: 4, flex: '0 0 auto',
              }} />
              {!last && <span style={{ width: 2, flex: 1, background: '#e4e7ec', marginTop: 2 }} />}
            </div>
            {/* card */}
            <div style={{ paddingBottom: last ? 0 : 18, flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 13, fontWeight: 700, color: meta.color, background: meta.bg,
                  padding: '2px 8px', borderRadius: 999,
                }}>{meta.label}</span>
                {showObject && e.obj_name && (
                  <span style={{ fontSize: 13, color: '#475467', fontWeight: 600 }}>{e.obj_name}</span>
                )}
                <span style={{ fontSize: 14, color: '#344054', fontWeight: 600 }}>{actor(e)}</span>
                <span style={{ fontSize: 13, color: '#98a2b3' }}>{formatWhen(e.changed_at)}</span>
                {e.client && (
                  <span style={{
                    fontSize: 13, fontWeight: 600, color: '#475467', background: '#eef1f4',
                    padding: '1px 7px', borderRadius: 999, textTransform: 'capitalize',
                  }}>via {e.client}{e.via_object && (
                    <span style={{ textTransform: 'none' }}> {e.via_object}</span>
                  )}</span>
                )}
                {e.client_ip && (
                  <span style={{ fontSize: 13, color: '#98a2b3' }}>· {e.client_ip}</span>
                )}
              </div>
              <div style={{ marginTop: 6 }}>
                <AuditDiff entry={e} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
